import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { gameAPI } from '../../services/gameAPI';
import { useGameStore } from '../../store/gameStore';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '../ui/dialog';
import { Loader2, Save, Play, Eye, Info, Trash2, Check, Copy, Infinity } from 'lucide-react';

export const UserDetailModal = ({
  open,
  onClose,
  user,  // user object from users list: { user_id, display_name, daily_case_limit, unlimited_cases, cases_generated, cases_today, ... }
  onUserUpdated,  // callback when user data changes
  onViewCaseInfo,  // callback to open CaseInfoModal: (caseId, sessionId) => void
  onError,  // callback for error toasts
}) => {
  const navigate = useNavigate();
  const loadSavedGame = useGameStore((state) => state.loadSavedGame);

  const [displayName, setDisplayName] = useState('');
  const [dailyLimit, setDailyLimit] = useState(2);
  const [unlimited, setUnlimited] = useState(false);
  const [savingLimit, setSavingLimit] = useState(false);
  const [savingName, setSavingName] = useState(false);
  const [recentCases, setRecentCases] = useState([]);
  const [casesLoading, setCasesLoading] = useState(false);
  const [copiedId, setCopiedId] = useState(null);
  const [loadingSession, setLoadingSession] = useState(null);
  const [deletingSession, setDeletingSession] = useState(null);

  // Initialize form when user changes
  useEffect(() => {
    if (user) {
      setDisplayName(user.display_name || '');
      setDailyLimit(user.daily_case_limit ?? 2);
      setUnlimited(user.unlimited_cases || false);
    }
  }, [user]);

  // Fetch recent cases when modal opens
  const fetchRecentCases = useCallback(async () => {
    if (!user?.user_id) return;
    try {
      setCasesLoading(true);
      const data = await gameAPI.getUserRecentCases(user.user_id);
      setRecentCases(data.cases || []);
    } catch (error) {
      console.error('Failed to fetch recent cases:', error);
      onError?.('Failed to load recent cases');
    } finally {
      setCasesLoading(false);
    }
  }, [user?.user_id]);

  useEffect(() => {
    if (open && user?.user_id) {
      fetchRecentCases();
    }
  }, [open, user?.user_id, fetchRecentCases]);

  const handleSaveLimit = async () => {
    try {
      setSavingLimit(true);
      await gameAPI.updateUserDailyLimit(user.user_id, dailyLimit, unlimited);
      onUserUpdated?.({
        ...user,
        daily_case_limit: unlimited ? null : dailyLimit,
        unlimited_cases: unlimited,
      });
    } catch (error) {
      onError?.(error.response?.data?.detail || 'Failed to update daily limit');
    } finally {
      setSavingLimit(false);
    }
  };

  const handleSaveName = async () => {
    try {
      setSavingName(true);
      await gameAPI.updateUserDisplayName(user.user_id, displayName);
      onUserUpdated?.({
        ...user,
        display_name: displayName.trim(),
      });
    } catch (error) {
      onError?.(error.response?.data?.detail || 'Failed to update display name');
    } finally {
      setSavingName(false);
    }
  };

  const handleCopyId = async (id) => {
    try {
      await navigator.clipboard.writeText(id);
      setCopiedId(id);
      setTimeout(() => setCopiedId(null), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  const handleLoadCase = async (sessionId) => {
    try {
      setLoadingSession(sessionId);
      const savedData = await gameAPI.loadGameSession(sessionId);
      loadSavedGame({
        theme: savedData.theme || 'noir',
        difficulty: savedData.difficulty || 'medium',
        caseId: savedData.caseId,
        sessionId: savedData.sessionId,
        gameState: savedData.gameState,
        messageHistory: savedData.messageHistory,
        guideCharacter: savedData.guideCharacter,
        caseData: savedData.caseData,
        lastImage: savedData.lastImage,
        lastImageVantage2: savedData.lastImageVantage2,
      });
      onClose();
      navigate('/game');
    } catch (error) {
      onError?.(error.response?.data?.detail || 'Failed to load case');
    } finally {
      setLoadingSession(null);
    }
  };

  const handleDeleteCase = async (sessionId, victimName) => {
    if (!window.confirm(`PERMANENTLY delete the case "${victimName}"?\n\nThis cannot be undone.`)) return;
    try {
      setDeletingSession(sessionId);
      // Find the case before removing it so we can check its date
      const deletedCase = recentCases.find(c => c.session_id === sessionId);
      await gameAPI.deleteGameSession(sessionId);
      setRecentCases(prev => prev.filter(c => c.session_id !== sessionId));
      // Only decrement cases_today if the deleted case was started today (in ET, matching backend)
      const etDate = (d) => new Intl.DateTimeFormat('en-US', { timeZone: 'America/New_York', year: 'numeric', month: '2-digit', day: '2-digit' }).format(new Date(d));
      const isToday = deletedCase?.started_at && etDate(deletedCase.started_at) === etDate(new Date());
      onUserUpdated?.({
        ...user,
        cases_generated: Math.max(0, (user.cases_generated ?? 0) - 1),
        cases_today: isToday ? Math.max(0, (user.cases_today ?? 0) - 1) : (user.cases_today ?? 0),
      });
    } catch (error) {
      onError?.(error.response?.data?.detail || 'Failed to delete case');
    } finally {
      setDeletingSession(null);
    }
  };

  const getThemeColors = (theme) => {
    const colors = {
      noir: 'bg-gray-800 text-gray-100',
      '90s_nyc': 'bg-orange-600 text-white',
      miami_vice: 'bg-pink-500 text-white',
    };
    return colors[theme] || colors.noir;
  };

  const formatThemeName = (theme) => {
    const names = { noir: '1940s Noir', '90s_nyc': '90s NYC', miami_vice: '80s Miami' };
    return names[theme] || theme;
  };

  const getStatusBadge = (status) => {
    const variants = {
      in_progress: { className: 'bg-blue-600 text-white', label: 'In Progress' },
      won: { className: 'bg-green-600 text-white', label: 'Solved' },
      lost: { className: 'bg-red-600 text-white', label: 'Failed' },
    };
    return variants[status] || variants.in_progress;
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Unknown';
    let normalized = dateString;
    if (typeof dateString === 'string' && !dateString.endsWith('Z') && !dateString.includes('+')) {
      normalized = dateString + 'Z';
    }
    return new Date(normalized).toLocaleDateString('en-US', {
      timeZone: 'America/New_York',
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  };

  if (!user) return null;

  return (
    <Dialog open={open} onOpenChange={(isOpen) => { if (!isOpen) onClose(); }}>
      <DialogContent className="sm:max-w-2xl bg-gray-900 border-gray-700 text-white">
        <DialogHeader>
          <DialogTitle className="text-white flex items-center gap-2">
            User Details
          </DialogTitle>
          <DialogDescription className="text-gray-400">
            <span className="font-mono text-xs">{user.user_id}</span>
            <button onClick={() => handleCopyId(user.user_id)} className="ml-2 inline-flex items-center text-gray-500 hover:text-white">
              {copiedId === user.user_id ? <Check className="w-3 h-3" /> : <Copy className="w-3 h-3" />}
            </button>
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6 mt-2">
          {/* Display Name */}
          <div className="space-y-2">
            <label className="text-sm font-medium text-gray-300">Display Name</label>
            <div className="flex gap-2">
              <input
                type="text"
                value={displayName}
                onChange={(e) => setDisplayName(e.target.value)}
                placeholder="Enter a name for this user..."
                className="flex-1 bg-gray-800 border border-gray-600 rounded-md px-3 py-2 text-sm text-white placeholder:text-gray-500 focus:outline-none focus:ring-2 focus:ring-amber-500"
              />
              <Button
                size="sm"
                onClick={handleSaveName}
                disabled={savingName || displayName.trim() === (user.display_name || '')}
                className="bg-amber-600 hover:bg-amber-500"
              >
                {savingName ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
              </Button>
            </div>
          </div>

          {/* Daily Limit */}
          <div className="space-y-2">
            <label className="text-sm font-medium text-gray-300">Daily Case Generation Limit</label>
            <div className="flex items-center gap-4">
              <div className="flex items-center gap-2">
                <input
                  type="number"
                  min="0"
                  value={dailyLimit}
                  onChange={(e) => setDailyLimit(Math.max(0, parseInt(e.target.value) || 0))}
                  disabled={unlimited}
                  className="w-20 bg-gray-800 border border-gray-600 rounded-md px-3 py-2 text-sm text-white focus:outline-none focus:ring-2 focus:ring-amber-500 disabled:opacity-50"
                />
                <span className="text-sm text-gray-400">per day</span>
              </div>
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={unlimited}
                  onChange={(e) => setUnlimited(e.target.checked)}
                  className="w-4 h-4 rounded border-gray-600 bg-gray-800 text-amber-500 focus:ring-amber-500"
                />
                <Infinity className="w-4 h-4 text-gray-400" />
                <span className="text-sm text-gray-300">Unlimited</span>
              </label>
              <Button
                size="sm"
                onClick={handleSaveLimit}
                disabled={savingLimit}
                className="bg-amber-600 hover:bg-amber-500"
              >
                {savingLimit ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
              </Button>
            </div>
          </div>

          {/* Stats */}
          <div className="grid grid-cols-2 gap-4">
            <div className="bg-gray-800/50 rounded-lg p-3 border border-gray-700">
              <div className="text-2xl font-bold text-white">{user.cases_generated ?? 0}</div>
              <div className="text-xs text-gray-400 uppercase tracking-wide">Total Cases</div>
            </div>
            <div className="bg-gray-800/50 rounded-lg p-3 border border-gray-700">
              <div className="text-2xl font-bold text-white">{user.cases_today ?? 0}</div>
              <div className="text-xs text-gray-400 uppercase tracking-wide">Cases Today</div>
            </div>
          </div>

          {/* Recent Cases */}
          <div className="space-y-2">
            <h3 className="text-sm font-medium text-gray-300">Recent Cases</h3>
            {casesLoading ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="w-6 h-6 animate-spin text-gray-400" />
              </div>
            ) : recentCases.length === 0 ? (
              <p className="text-sm text-gray-500 py-4 text-center">No cases found</p>
            ) : (
              <div className="space-y-2">
                {recentCases.map((caseItem) => {
                  const statusInfo = getStatusBadge(caseItem.status);
                  return (
                    <div
                      key={caseItem.session_id}
                      className="bg-gray-800/50 rounded-lg border border-gray-700 p-3 flex items-center justify-between gap-3"
                    >
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="text-sm font-medium text-white truncate">
                            {caseItem.victim_name}
                          </span>
                          <Badge className={`text-xs ${getThemeColors(caseItem.theme)}`}>
                            {formatThemeName(caseItem.theme)}
                          </Badge>
                          <Badge variant="outline" className="text-xs capitalize text-gray-300 border-gray-600">
                            {caseItem.difficulty}
                          </Badge>
                          <Badge className={`text-xs ${statusInfo.className}`}>
                            {statusInfo.label}
                          </Badge>
                        </div>
                        <div className="flex items-center gap-3 mt-1 text-xs text-gray-500">
                          <span>{formatDate(caseItem.started_at)}</span>
                          <span>Turn {caseItem.turn_count}/{caseItem.max_turns}</span>
                          <button onClick={() => handleCopyId(caseItem.case_id)} className="flex items-center gap-1 hover:text-white" title="Copy case ID">
                            {copiedId === caseItem.case_id ? <Check className="w-3 h-3" /> : <Copy className="w-3 h-3" />}
                            <span className="font-mono">{caseItem.case_id?.slice(0, 8)}</span>
                          </button>
                          <button onClick={() => handleCopyId(caseItem.session_id)} className="flex items-center gap-1 hover:text-white" title="Copy session ID">
                            {copiedId === caseItem.session_id ? <Check className="w-3 h-3" /> : <Copy className="w-3 h-3" />}
                            <span className="font-mono">S:{caseItem.session_id?.slice(0, 6)}</span>
                          </button>
                        </div>
                      </div>
                      <div className="flex items-center gap-1">
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-8 w-8 p-0 text-gray-400 hover:text-white"
                          onClick={() => handleLoadCase(caseItem.session_id)}
                          disabled={loadingSession === caseItem.session_id}
                          title={caseItem.status === 'in_progress' ? 'Continue' : 'Review'}
                        >
                          {loadingSession === caseItem.session_id ? (
                            <Loader2 className="w-4 h-4 animate-spin" />
                          ) : caseItem.status === 'in_progress' ? (
                            <Play className="w-4 h-4" />
                          ) : (
                            <Eye className="w-4 h-4" />
                          )}
                        </Button>
                        {onViewCaseInfo && (
                          <Button
                            variant="ghost"
                            size="sm"
                            className="h-8 w-8 p-0 text-gray-400 hover:text-white"
                            onClick={() => onViewCaseInfo(caseItem.case_id, caseItem.session_id)}
                            title="Case Info"
                          >
                            <Info className="w-4 h-4" />
                          </Button>
                        )}
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-8 w-8 p-0 text-red-400 hover:text-red-300 hover:bg-red-900/30"
                          onClick={() => handleDeleteCase(caseItem.session_id, caseItem.victim_name)}
                          disabled={deletingSession === caseItem.session_id}
                          title="Delete case"
                        >
                          {deletingSession === caseItem.session_id ? (
                            <Loader2 className="w-4 h-4 animate-spin" />
                          ) : (
                            <Trash2 className="w-4 h-4" />
                          )}
                        </Button>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
